import * as React from "react";
import { ArrowLeft, MessageSquare, Plus } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router";
import { useCreateNewConversationMutation } from "@/modules/chat/chat.api";
import { CircleLoader } from "@/components/ui/circleLoader";
import { useCurrentPdf } from "@/modules/chat/hooks/useCurrentPdf";
import { FileItem, FolderItem } from "@/types/fse";
import { ChatHeaderBreadcrumbs } from "./ChatHeaderBreadcrumbs";

interface Props {
  conversationTitle?: string;
  systemId: string;
  fsEntry: FileItem | FolderItem;
}

export const ChatHeader: React.FC<Props> = ({ fsEntry, systemId, conversationTitle = "New Conversation" }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setRightDrawerContent, setArtefacts, setSelectedArtefactId } = useCurrentPdf();
  const rawFromSnapshot = (location.state as { fromSnapshot?: string } | null)?.fromSnapshot;
  const fromSnapshot =
    typeof rawFromSnapshot === "string" &&
    (rawFromSnapshot.startsWith("/snapshots") || /^\/contract-families(?:\/|\?|$)/.test(rawFromSnapshot))
      ? rawFromSnapshot
      : undefined;
  const returnLabel = fromSnapshot?.startsWith("/contract-families") ? "Contract families" : "Snapshot";

  const [handleLastConversationId, { isLoading }] = useCreateNewConversationMutation();

  const onNewConversation = React.useCallback(async () => {
    setRightDrawerContent("pdf");
    setSelectedArtefactId(null);
    setArtefacts([]);
    const data = await handleLastConversationId(systemId).unwrap();
    navigate(`/chat/${systemId}/${data}`, fromSnapshot ? { state: { fromSnapshot } } : undefined);
  }, [
    setRightDrawerContent,
    setSelectedArtefactId,
    setArtefacts,
    handleLastConversationId,
    systemId,
    navigate,
    fromSnapshot,
  ]);

  return (
    <div className="flex items-center justify-between gap-3 p-2">
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex items-center gap-2">
          <MessageSquare size={20} className="shrink-0 text-gray-600" />
          <h1 className="truncate text-lg font-medium text-gray-900">{conversationTitle}</h1>
        </div>

        <ChatHeaderBreadcrumbs fsEntry={fsEntry} />
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {fromSnapshot && (
          <Link
            to={fromSnapshot}
            className="flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200"
          >
            <ArrowLeft size={16} className="shrink-0" />
            <span className="hidden sm:inline">{returnLabel}</span>
          </Link>
        )}
        <button
          onClick={onNewConversation}
          className="flex items-center gap-2 whitespace-nowrap rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white transition-colors"
          title="Start new conversation"
          disabled={isLoading}
        >
          {isLoading ? <CircleLoader /> : <Plus size={16} className="shrink-0" />}
          <span className="hidden sm:inline">{isLoading ? "Refreshing..." : "Refresh"}</span>
        </button>
      </div>
    </div>
  );
};
